// Collection Boy master form: defaults, client-side checks and the request body. Pure helpers, so they are tested alone.
// The server checks everything again; these checks only save a round trip and say what is wrong next to the field.

export const ID_PROOF_TYPES = ['Aadhaar', 'PAN', 'Driving Licence', 'Voter ID', 'Passport', 'Other'];
export const EMPLOYMENT_TYPES = ['Employee', 'Freelance'];
export const GENDERS = ['Male', 'Female', 'Other'];

export const EMPTY_COLLECTION_BOY = {
  collectionBoyId: 0,
  boyCode: '',
  centreId: '',
  boyName: '',
  mobile: '',
  altMobile: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  address: '',
  city: '',
  pincode: '',
  idProofType: '',
  idProofNumber: '',
  joiningDate: '',
  employmentType: 'Employee',
  vehicleNo: '',
  remarks: '',
  isActive: true,
};

const dateOnly = (v) => (v ? String(v).substring(0, 10) : '');

export const formFromRecord = (r) => ({
  ...EMPTY_COLLECTION_BOY,
  collectionBoyId: r.collectionBoyId,
  boyCode: r.boyCode || '',
  centreId: r.centreId || '',
  boyName: r.boyName || '',
  mobile: r.mobile || '',
  altMobile: r.altMobile || '',
  email: r.email || '',
  gender: r.gender || '',
  dateOfBirth: dateOnly(r.dateOfBirth),
  address: r.address || '',
  city: r.city || '',
  pincode: r.pincode || '',
  idProofType: r.idProofType || '',
  idProofNumber: r.idProofNumber || '',
  joiningDate: dateOnly(r.joiningDate),
  employmentType: r.employmentType || 'Employee',
  vehicleNo: r.vehicleNo || '',
  remarks: r.remarks || '',
  isActive: r.isActive !== false,
});

/** The ten digits of an Indian mobile number typed in any common way, or '' when it is not one. */
export const normaliseMobile = (value) => {
  const raw = String(value || '');
  // a digit of another script (Arabic-Indic, Devanagari ...) is not accepted, the same as on the server
  if (/(?![0-9])\p{Nd}/u.test(raw)) return '';
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d.length === 10 && d[0] >= '6' ? d : '';
};

export const validateCollectionBoy = (f, today = new Date()) => {
  const errs = {};
  if ((f.boyName || '').trim().length < 2) errs.boyName = 'Name is required.';
  const mobile = normaliseMobile(f.mobile);
  if (!mobile) errs.mobile = 'Enter a valid 10 digit mobile number.';
  if (f.altMobile && f.altMobile.trim()) {
    const alt = normaliseMobile(f.altMobile);
    if (!alt) errs.altMobile = 'Enter a valid 10 digit mobile number.';
    else if (alt === mobile) errs.altMobile = 'Same as the mobile number.';
  }
  if (f.email && f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) errs.email = 'Email is not valid.';
  if (f.dateOfBirth) {
    const dob = new Date(f.dateOfBirth);
    const years = (today - dob) / (365.25 * 24 * 3600 * 1000);
    if (years < 16 || years > 80) errs.dateOfBirth = 'Age must be between 16 and 80 years.';
  }
  if (f.joiningDate && new Date(f.joiningDate) > new Date(today.getTime() + 24 * 3600 * 1000)) errs.joiningDate = 'Joining date cannot be in the future.';
  if (f.idProofNumber && f.idProofNumber.trim() && !f.idProofType) errs.idProofType = 'Choose the ID proof type.';
  if (f.idProofType === 'Aadhaar' && f.idProofNumber && f.idProofNumber.replace(/\D/g, '').length !== 12) errs.idProofNumber = 'Aadhaar must have 12 digits.';
  if (f.idProofType === 'PAN' && f.idProofNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.idProofNumber.trim().toUpperCase())) errs.idProofNumber = 'PAN must look like ABCDE1234F.';
  if (f.pincode && f.pincode.replace(/\D/g, '').length !== 6) errs.pincode = 'PIN code must have 6 digits.';
  return errs;
};

const orNull = (v) => {
  const t = (v ?? '').toString().trim();
  return t === '' ? null : t;
};

export const toPayload = (f) => ({
  centreId: f.centreId ? Number(f.centreId) : null,
  boyName: f.boyName.trim(),
  mobile: f.mobile.trim(),
  altMobile: orNull(f.altMobile),
  email: orNull(f.email),
  gender: orNull(f.gender),
  dateOfBirth: orNull(f.dateOfBirth),
  address: orNull(f.address),
  city: orNull(f.city),
  pincode: orNull(f.pincode),
  idProofType: orNull(f.idProofType),
  idProofNumber: orNull(f.idProofNumber),
  joiningDate: orNull(f.joiningDate),
  employmentType: f.employmentType || 'Employee',
  vehicleNo: orNull(f.vehicleNo),
  remarks: orNull(f.remarks),
  isActive: !!f.isActive,
});
